import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaEnvelope, FaArrowLeft } from 'react-icons/fa';
import Turnstile from 'react-turnstile';

import logo from '../assets/logo.png';

const ForgotPassword = () => {
    const { resetPassword } = useAuth();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [message, setMessage] = useState('');
    const [captchaToken, setCaptchaToken] = useState(null);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [turnstileKey, setTurnstileKey] = useState(0); // Used to force remount/reset
    const turnstileRef = useRef();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setMessage('');

        // Simple client-side check
        if (!captchaToken) {
            setStatus('error');
            setMessage('Please complete the security check (Captcha).');
            setShowCaptcha(true);
            return;
        }

        try {
            const { error } = await resetPassword(email, { captchaToken });
            if (error) throw error;
            setStatus('success');
            setMessage('Password reset link has been sent to your email.');
        } catch (error) {
            setStatus('error');
            setMessage(error.message);
            setTurnstileKey(prev => prev + 1); // Force remount
            setCaptchaToken(null);
        }
    };

    return (
        <div className="min-h-screen bg-white dark:bg-[#0a1220] flex flex-col justify-start pt-24 sm:pt-32 px-6 lg:px-8 transition-colors duration-300">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <Link to="/" className="flex justify-center mb-6">
                    <img
                        src={logo}
                        alt="DataHelix Logo"
                        className="h-12 w-auto drop-shadow-lg hover:scale-105 transition-transform"
                    />
                </Link>
                <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-white">
                    Reset your password
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    Enter your email and we'll send you a link to reset your password.
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white dark:bg-[#162032] py-8 px-4 shadow-xl rounded-xl border border-gray-100 dark:border-gray-800 sm:px-10 transition-colors duration-300">
                    {status === 'success' ? (
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                                <FaEnvelope className="h-6 w-6 text-green-600 dark:text-green-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Check your email</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                {message}
                            </p>
                            <Link
                                to="/login"
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200"
                            >
                                Return to Login
                            </Link>
                        </div>
                    ) : (
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Email address
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FaEnvelope className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-[#0a1220] placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-colors duration-200"
                                        placeholder="you@example.com"
                                    />
                                </div>
                            </div>

                            {status === 'error' && (
                                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800">
                                    <div className="flex">
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                                                Error sending reset link
                                            </h3>
                                            <div className="mt-2 text-sm text-red-700 dark:text-red-400">
                                                <p>{message}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Captcha: Invisible by default */}
                            <div className="flex justify-center pt-2">
                                <Turnstile
                                    key={turnstileKey}
                                    ref={turnstileRef}
                                    sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"}
                                    onVerify={(token) => setCaptchaToken(token)}
                                    onError={() => setMessage("CAPTCHA failed. Please try again.")}
                                    onExpire={() => setCaptchaToken(null)}
                                    theme="dark"
                                    appearance={showCaptcha ? "always" : "interaction-only"}
                                />
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${status === 'loading'
                                        ? 'bg-orange-400 cursor-not-allowed'
                                        : 'bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500'
                                        } transition-all duration-200`}
                                >
                                    {status === 'loading' ? 'Sending...' : 'Send Reset Link'}
                                </button>
                            </div>
                        </form>
                    )}

                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white dark:bg-[#162032] text-gray-500 dark:text-gray-400">
                                    Remember your password?
                                </span>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-center">
                            <Link to="/login" className="flex items-center text-sm font-medium text-orange-600 hover:text-orange-500 dark:text-orange-400 dark:hover:text-orange-300 transition-colors">
                                <FaArrowLeft className="mr-2 h-3 w-3" />
                                Back to Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
